import React, { useMemo } from 'react';
import { t } from '@lingui/macro';
import { useQuery } from '@apollo/client';
import useSentinelTeam from 'hooks/useSentinelTeam';

import Grid from '@decisiv/ui-components/lib/components/Grid';

import DashboardSection from 'components/DashboardSection';
import Loading from 'components/Loading';

import AlertsProvider from 'pages/Alerts/AlertsContext';

import TotalAssetsCard from './TotalAssetsCard';
import TotalAssetGroupingsCard from './TotalAssetGroupingsCard';
import AssetsByStatusCard from './AssetsByStatusCard';
import ScheduledMaintenanceCard from './ScheduledMaintenanceCard';
import FaultsBySeverityCard from './FaultsBySeverityCard';
import ServiceEventsCard from './ServiceEventsCard';
import PendingApprovalsCard from './PendingApprovalsCard';
import AlertsByTypeCard from './AlertsByTypeCard';
import MostAlertsByDepotCard from './MostAlertsByDepotCard';

import businessOverviewPreferencesQuery from './businessOverviewPreferences.graphql';

const { Row } = Grid;

const useBusinessOverviewPreferences = () => {
  return useQuery(businessOverviewPreferencesQuery, {
    fetchPolicy: 'cache-and-network',
  });
};

function BusinessOverview() {
  const { isFleet } = useSentinelTeam();
  const { loading, data } = useBusinessOverviewPreferences();

  const { dashboardPreferences } = data || {};

  const {
    totals,
    assetsByStatus,
    serviceEvents,
    alertsByType,
    mostAlertsByDepot,
    scheduledMaintenance,
    pendingApprovals,
    faultsBySeverity,
  } = dashboardPreferences || {};

  const lastRowActiveCards = useMemo(
    () =>
      [alertsByType, mostAlertsByDepot, scheduledMaintenance].filter(Boolean)
        .length,
    [alertsByType, mostAlertsByDepot, scheduledMaintenance],
  );

  if (loading) return <Loading />;

  return (
    <DashboardSection title={t`Business Overview`}>
      <>
        <Grid.Container padding={0}>
          {totals && (
            <Row>
              <Grid.Column span={6} marginTop={2}>
                <TotalAssetsCard />
              </Grid.Column>
              <Grid.Column span={6} marginTop={2}>
                <TotalAssetGroupingsCard />
              </Grid.Column>
            </Row>
          )}
          {(assetsByStatus || serviceEvents) && (
            <Row>
              {assetsByStatus && (
                <Grid.Column span={6} marginTop={2}>
                  <AlertsProvider>
                    <AssetsByStatusCard />
                  </AlertsProvider>
                </Grid.Column>
              )}
              {serviceEvents && (
                <Grid.Column span={6} marginTop={2}>
                  <ServiceEventsCard />
                </Grid.Column>
              )}
            </Row>
          )}
          {(alertsByType || mostAlertsByDepot || scheduledMaintenance) && (
            <Row>
              {alertsByType && (
                <Grid.Column span={4} marginTop={2}>
                  <AlertsByTypeCard
                    key={lastRowActiveCards}
                    showDividers={lastRowActiveCards === 1}
                  />
                </Grid.Column>
              )}
              {mostAlertsByDepot && (
                <Grid.Column span={4} marginTop={2}>
                  <MostAlertsByDepotCard />
                </Grid.Column>
              )}
              {scheduledMaintenance && (
                <Grid.Column span={4} marginTop={2}>
                  <ScheduledMaintenanceCard />
                </Grid.Column>
              )}
            </Row>
          )}
          {isFleet && pendingApprovals && (
            <Row>
              <Grid.Column span={4} marginTop={2}>
                <PendingApprovalsCard />
              </Grid.Column>
              <Grid.Column span={4} marginTop={2}>
                {isFleet && faultsBySeverity && <FaultsBySeverityCard />}
              </Grid.Column>
              <Grid.Column span={4} marginTop={2} />
            </Row>
          )}
        </Grid.Container>
      </>
    </DashboardSection>
  );
}

export default BusinessOverview;
